import { useState } from 'react';
import type { AddressInput, CustomerApiClient } from '../api-client.ts';
import { BottomSheet, Button, Card, EmptyState } from '@beautyburst/ui';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';

type Addresses = Awaited<ReturnType<CustomerApiClient['addresses']['list']>>;
type Address = Addresses[number];

const blankAddress: AddressInput = {
  label: '',
  recipientName: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  region: '',
  postalCode: '',
  deliveryNotes: '',
};

export interface AddressesScreenProps {
  addresses: Addresses;
  client: Pick<CustomerApiClient, 'addresses'>;
  onChanged?: () => void | Promise<void>;
}

/** Address mutations depend on the customer API and never request device location. */
export function AddressesScreen({ addresses, client, onChanged }: AddressesScreenProps) {
  const [draft, setDraft] = useState<AddressInput>(blankAddress);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Address | null>(null);
  const refresh = () => {
    void onChanged?.();
  };
  const update = <Key extends keyof AddressInput>(key: Key, value: AddressInput[Key]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (editingId === null) await client.addresses.create(draft);
    else await client.addresses.update(editingId, draft);
    setDraft(blankAddress);
    setEditingId(null);
    refresh();
  };

  return (
    <section aria-labelledby="addresses-title">
      <h1
        id="addresses-title"
        style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}
      >
        Addresses
      </h1>
      <form onSubmit={(event) => void submit(event)} style={{ display: 'grid', gap: space[3] }}>
        <h2 style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}>
          {editingId === null ? 'Add an address' : 'Edit address'}
        </h2>
        <label>
          Label
          <input
            required
            value={draft.label}
            onChange={(event) => update('label', event.currentTarget.value)}
          />
        </label>
        <label>
          Recipient name
          <input
            required
            autoComplete="name"
            value={draft.recipientName}
            onChange={(event) => update('recipientName', event.currentTarget.value)}
          />
        </label>
        <label>
          Phone number (optional)
          <input
            type="tel"
            autoComplete="tel"
            value={draft.phone ?? ''}
            onChange={(event) => update('phone', event.currentTarget.value)}
          />
        </label>
        <label>
          Address line 1
          <input
            required
            value={draft.line1}
            onChange={(event) => update('line1', event.currentTarget.value)}
          />
        </label>
        <label>
          Address line 2 (optional)
          <input
            value={draft.line2}
            onChange={(event) => update('line2', event.currentTarget.value)}
          />
        </label>
        <label>
          City
          <input
            required
            value={draft.city}
            onChange={(event) => update('city', event.currentTarget.value)}
          />
        </label>
        <label>
          State
          <input
            required
            value={draft.region}
            onChange={(event) => update('region', event.currentTarget.value)}
          />
        </label>
        <label>
          ZIP code
          <input
            required
            value={draft.postalCode}
            onChange={(event) => update('postalCode', event.currentTarget.value)}
          />
        </label>
        <label>
          Delivery notes (optional)
          <input
            value={draft.deliveryNotes}
            onChange={(event) => update('deliveryNotes', event.currentTarget.value)}
          />
        </label>
        <div style={{ display: 'flex', gap: space[3] }}>
          <Button type="submit">{editingId === null ? 'Save address' : 'Save changes'}</Button>
          {editingId === null ? null : (
            <Button
              variant="ghost"
              onClick={() => {
                setEditingId(null);
                setDraft(blankAddress);
              }}
            >
              Cancel edit
            </Button>
          )}
        </div>
      </form>

      <section aria-labelledby="saved-addresses-title" style={{ marginTop: space[8] }}>
        <h2
          id="saved-addresses-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Saved addresses
        </h2>
        {addresses.length === 0 ? (
          <EmptyState
            title="No saved addresses"
            description="Add an address to check serviceability."
          />
        ) : (
          <ul
            aria-label="Saved addresses"
            style={{ display: 'grid', gap: space[3], listStyle: 'none', padding: 0 }}
          >
            {addresses.map((address) => (
              <li key={address.id}>
                <Card>
                  <h3
                    style={{
                      ...typeStyle('headingSm'),
                      color: semanticColor.textPrimary,
                      marginTop: 0,
                    }}
                  >
                    {address.label ?? 'Address'}
                    {address.is_default ? ' · Default' : ''}
                  </h3>
                  <p>
                    {[
                      address.line1,
                      address.line2,
                      `${address.city}, ${address.region} ${address.postal_code}`,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
                    Serviceability: {address.serviceability.replaceAll('_', ' ').toLowerCase()}
                  </p>
                  <div style={{ display: 'flex', gap: space[3], flexWrap: 'wrap' }}>
                    {!address.is_default ? (
                      <Button
                        variant="secondary"
                        onClick={() => void client.addresses.setDefault(address.id).then(refresh)}
                      >
                        Set as default
                      </Button>
                    ) : null}
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEditingId(address.id);
                        setDraft({
                          label: address.label ?? '',
                          recipientName: address.recipient_name,
                          phone: address.phone ?? '',
                          line1: address.line1,
                          line2: address.line2 ?? '',
                          city: address.city,
                          region: address.region,
                          postalCode: address.postal_code,
                          deliveryNotes: address.delivery_notes ?? '',
                        });
                      }}
                    >
                      Edit
                    </Button>
                    <Button variant="ghost" onClick={() => setDeleteTarget(address)}>
                      Delete
                    </Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <BottomSheet
        open={deleteTarget !== null}
        title="Delete address?"
        onClose={() => setDeleteTarget(null)}
        footer={
          <div style={{ display: 'flex', gap: space[3] }}>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Keep address
            </Button>
            <Button
              onClick={() => {
                if (deleteTarget === null) return;
                void client.addresses.delete(deleteTarget.id).then(() => {
                  setDeleteTarget(null);
                  refresh();
                });
              }}
            >
              Delete address
            </Button>
          </div>
        }
      >
        <p>Delete {deleteTarget?.label ?? 'this address'}? This cannot be undone.</p>
      </BottomSheet>
    </section>
  );
}
